/**
 * Liquid filetype used just for automatic minification.
 * NO LIQUID CODE SHOULD GO HERE. Keep it pure JS.
 */

const TAPMOD_ORIGIN = 'https://signitas-wrap.tapmod.io'
const TAPMOD_USE_STRICT_ORIGIN = false

// Local state used for intermediate values
const state = {
  camera: null,
}

/**
 * Returns the contentWindow of the active 3D viewer iframe.
 *
 * @returns {Window|null} The iframe window when available.
 */
function get3dFrameWindow () {
  const frame = window?.meta3D?.iframeEl
  if (!frame || !frame[0] || !frame[0].contentWindow) return null
  return frame[0].contentWindow
}

/**
 * Resolves the current iframe origin used by the 3D viewer.
 *
 * @returns {string} A valid origin for postMessage targeting.
 */
function get3dFrameOrigin () {
  const frame = window?.meta3D?.iframeEl
  const frameElement = frame && frame[0]
  if (!frameElement) return TAPMOD_ORIGIN

  const frameSrc = frameElement.currentSrc || frameElement.src
  if (!frameSrc) return TAPMOD_ORIGIN

  try {
    return new URL(frameSrc, window.location.href).origin
  } catch (error) {
    return TAPMOD_ORIGIN
  }
}

/**
 * Sends a message to the 3D viewer iframe.
 *
 * @param {string} call - Viewer command name.
 * @param {object} value - Command payload.
 * @returns {boolean} True when the message was sent.
 */
function postTo3dViewer (call, value) {
  const frameWindow = get3dFrameWindow()
  if (!frameWindow) return false

  frameWindow.postMessage(
    { call, value },
    TAPMOD_USE_STRICT_ORIGIN ? get3dFrameOrigin() : '*'
  )
  return true
}

/**
 * Sends a property update to the 3D viewer.
 *
 * @param {string} param - Viewer parameter name.
 * @param {*} value - Parameter value.
 * @returns {boolean} True when the message was sent.
 */
function set3dProp (param, value) {
  return postTo3dViewer('tapmodSetChange', { param, value })
}

/**
 * Sets the current camera or editing position in the 3D viewer.
 *
 * @param {string|number} value - Viewer position identifier.
 * @returns {boolean} True when the message was sent.
 */
function set3dPosition (value) {
  if (state.camera === value) return true

  state.camera = value
  return postTo3dViewer('tapmodSetPosition', { value })
}

/**
 * Loads an image element and resolves when it is ready.
 *
 * @param {string} src - Image URL or data URL.
 * @returns {Promise<HTMLImageElement>} The loaded image.
 */
function loadImage (src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}


/**
 * Converts a file input Blob into a data URL.
 *
 * @param {Blob} file - Uploaded file.
 * @returns {Promise<string>} Base64-encoded file contents.
 */
async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
    reader.readAsDataURL(file);
  });
}

/**
 * Performs a case-insensitive substring check.
 *
 * @param {string} str - Source string.
 * @param {string} val - Value to search for.
 * @returns {boolean} True when the value is present.
 */
function strHasVal (str, val) {
  return str.toLowerCase().indexOf(val.toLowerCase()) >= 0;
}

/**
 * Product page controller that coordinates variants, Bold options,
 * gallery state, pricing, and the Tapmod 3D viewer integration.
 *
 * @param {jQuery} item - Product container element.
 * @param {object} product - Shopify product JSON payload.
 * @returns {*} The initialized controller instance.
 */
function ProductJs( item, product ) {
    let optionsMap = {};
    let optionsMapAll = {};
    let productJs = this;
    const shopSettings = window.shopSettings
    const meta3D = window.meta3D
    const priceContainer = '.bold_option_price_display'
    const eventNamespace = '.productJs3d'
    const variantByIdMap = {}
    const variantBySkuMap = {}
    let priceObserver = null
    let priceUpdateFrame = null
    let messageListener = null

    const arrows = {
      v: {
        prev: `<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-arrow-up up-arrow" viewBox="0 0 32 32"><path fill="${shopSettings.content_text_color}" d="M26.984 23.5l1.516-1.617L16 8.5 3.5 21.883 5.008 23.5 16 11.742z"/></svg>`,
        next: `<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-arrow-down down-arrow" viewBox="0 0 32 32"><path fill="${shopSettings.content_text_color}" d="M26.984 8.5l1.516 1.617L16 23.5 3.5 10.117 5.008 8.5 16 20.258z"/></svg>`
      },
      h: {
        prev: `<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-arrow-left left-arrow" viewBox="0 0 32 32"><path fill="${shopSettings.content_text_color}" d="M24.333 28.205l-1.797 1.684L7.666 16l14.87-13.889 1.797 1.675L11.269 16z"/></svg>`,
        next: `<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-arrow-right right-arrow" viewBox="0 0 32 32"><path fill="${shopSettings.content_text_color}" d="M7.667 3.795l1.797-1.684L24.334 16 9.464 29.889l-1.797-1.675L20.731 16z"/></svg>`
      }
    }

    productJs.config = {
      gallery: {
        galleryGroupByOptions: shopSettings.product_thumbs_group_by.split(',').map(s => s.trim()).filter(v => v !== ''),
        galleryGroupMatchMode: shopSettings.product_thumbs_group_mode, //1 Match Everything, 2 Match Any, 4 Exclusive OR
        slickMainOptions: {
          slidesToShow: 1,
          slidesToScroll: 1,
          speed: 700,
          fade: shopSettings.product_main_image_effect === 'fade',
          waitForAnimate: false,
          prevArrow: arrows.h.prev,
          nextArrow: arrows.h.next,
          responsive: [
            {
              breakpoint: 900,
              settings: {
                dots: true,
                arrows: false
              }
            }
          ]
        },
        slickThumbsOptions: {
          autoplay: false,
          speed: 700,
          slidesToShow: 5,
          slidesToScroll: 1,
          infinite: false,
          vertical: shopSettings.product_thumbs_slide === 'vertical',
          verticalSwiping: shopSettings.product_thumbs_slide === 'vertical',
          prevArrow: shopSettings.product_thumbs_slide === 'vertical' ? arrows.v.prev : arrows.h.prev,
          nextArrow: shopSettings.product_thumbs_slide === 'vertical' ? arrows.v.next : arrows.h.next,
          responsive: [
            {
              breakpoint: 600,
              settings: {
                slidesToShow: 6,
                slidesToScroll: 1,
                vertical: false,
                verticalSwiping: false,
                prevArrow: arrows.h.prev,
                nextArrow: arrows.h.next
              }
            },
            {
              breakpoint: 900,
              settings: {
                slidesToShow: 10,
                slidesToScroll: 1,
                vertical: false,
                verticalSwiping: false,
                prevArrow: arrows.h.prev,
                nextArrow: arrows.h.next
              }
            }
          ]
        }
      }
    };

    /**
     * Converts a label-like string into a loose handle used by the theme.
     *
     * @param {string} value - Raw display value.
     * @returns {string} Normalized handle string.
     */
    function handleString(value) {
      return value.toLowerCase().replace(/ +/g,'-').replace(/[0-9]/g,'').replace(/[^a-z0-9-_]/g,'').trim()
    }

    /**
     * Checks whether the current product supports the 3D viewer.
     *
     * @returns {boolean} True when the product has the 3d-model tag.
     */
    function is3dProduct () {
      return $.inArray('3d-model', productJs.getProduct().tags) !== -1
    }

    /**
     * Returns the contentWindow of the currently active viewer iframe.
     *
     * @returns {Window|null} The viewer frame window.
     */
    function getCurrentFrameWindow () {
      return get3dFrameWindow()
    }

    /**
     * Reads the visible Bold option title from an event target.
     *
     * @param {HTMLElement} target - Changed input element.
     * @returns {string} Bold option title.
     */
    function getBoldOptionTitle (target) {
      return $(target).closest('.bold_option').find('.bold_option_title').text().trim()
    }

    /**
     * Maps a Bold option title to the viewer side identifier.
     *
     * @param {string} title - Visible option title.
     * @returns {string} left, right, front, back, face, or empty string.
     */
    function getSideFromTitle (title) {
      if (strHasVal(title, 'left')) return 'left'
      if (strHasVal(title, 'right')) return 'right'
      if (strHasVal(title, 'face profile 1')) return 'front'
      if (strHasVal(title, 'face profile 2') || strHasVal(title, 'back text')) return 'back'
      if (strHasVal(title, 'year')) return 'face'

      return ''
    }

    /**
     * Mirrors native Shopify variant selector changes to the 3D viewer.
     *
     * @param {HTMLElement} target - Changed select element.
     * @returns {void}
     */
    function syncViewerFromSelectorChange (target) {
      const selectorWrapper = $(target).closest('.selector-wrapper')
      let labelText = selectorWrapper.find('label').text()

      if (!labelText) {
        labelText = selectorWrapper.closest('.options').find('label').text()
      }

      if (strHasVal(labelText, 'Ring Dimensions')) {
        setDimension($(target).val())
      } else if (strHasVal(labelText, 'Metal')) {
        setMetal($(target).val())
      }
    }

    /**
     * Schedules a single total-price refresh on the next animation frame.
     *
     * @returns {void}
     */
    function scheduleTotalPriceUpdate () {
      if (priceUpdateFrame !== null) return

      priceUpdateFrame = window.requestAnimationFrame(function () {
        priceUpdateFrame = null
        updateTotalPrice()
      })
    }

    /**
     * Binds one-time UI events used by the viewer and Bold option sync.
     *
     * @returns {void}
     */
    function bindViewerEvents () {
      item.off(eventNamespace)
      $('body').off(eventNamespace)

      if (is3dProduct()) {
        item.on('change' + eventNamespace, '.selector-wrapper select', function(e) {
          syncViewerFromSelectorChange(e.target)
        })
      }

      item.on(
        'change' + eventNamespace,
        '.bold_options .bold_option:not(.bold_option_hidden):visible select, ' +
        '.bold_options .bold_option:not(.bold_option_hidden):visible input[type="file"], ' +
        '.bold_options .bold_option:not(.bold_option_hidden):visible input[type="radio"], ' +
        '.bold_options .bold_option:not(.bold_option_hidden):visible input[type="checkbox"]',
        handleBoldOptionChange
      )
      item.on('keyup' + eventNamespace, '.bold_options .bold_option .bold_option_element input[type="text"]', handleBoldTextInput)

      $('#open-3d-model-popup').off(eventNamespace).on('click' + eventNamespace, function() {
        $('body.templateProduct').addClass('model-3d-popup')
      })

      $('body').on('click' + eventNamespace, '.close-popup', function() {
        $('body.templateProduct').removeClass('model-3d-popup')
      })
    }

    /**
     * Applies the initial viewer state after the iframe reports readiness.
     *
     * @returns {void}
     */
    function handleSceneReady () {
      const meta = window.meta3D
      meta['left-artwork'] && setDefaultArtwork('Left', meta['left-artwork'], meta.left_artwork_type)
      meta['right-artwork'] && setDefaultArtwork('Right', meta['right-artwork'], meta.right_artwork_type)
      meta.face_engraving && setDefaultArtwork('face', meta.face_engraving, 'face')

      meta.face_profile_1 && setDefaultFaceProfile('Face Profile 1', meta.face_profile_1)
      meta.face_profile_2 && setDefaultFaceProfile('Face Profile 2', meta.face_profile_2)

      if (meta?.face_upload_placeholder) {
        set3dProp('artwork', { pos: 'face', path: meta.placeholder[meta.face_upload_placeholder] })
      }

      faceUploadExtend()

      setTimeout(function() {
        $('#loader').addClass('hide');
        set3dPosition(0);
      }, 1200)
    }

    /**
     * Binds the cross-frame message listener for the viewer handshake.
     *
     * @returns {void}
     */
    function bindMessageListener () {
      if (messageListener) {
        window.removeEventListener('message', messageListener)
      }

      messageListener = function(event) {
        if (event.source !== getCurrentFrameWindow()) return
        if (TAPMOD_USE_STRICT_ORIGIN) {
          const allowedOrigins = [TAPMOD_ORIGIN, get3dFrameOrigin()]
          if (allowedOrigins.indexOf(event.origin) === -1) return
        }
        if (event.data !== 'sceneIsReady') return

        handleSceneReady()
      }

      window.addEventListener('message', messageListener, false)
    }

    /**
     * Removes observers, timers, and DOM bindings created by ProductJs.
     *
     * @returns {void}
     */
    function destroyProductJs() {
      item.off(eventNamespace)
      $('body').off(eventNamespace)

      if (priceObserver) {
        priceObserver.disconnect()
        priceObserver = null
      }

      if (priceUpdateFrame !== null) {
        window.cancelAnimationFrame(priceUpdateFrame)
        priceUpdateFrame = null
      }

      if (messageListener) {
        window.removeEventListener('message', messageListener)
        messageListener = null
      }

      boldOptionsObserver.disconnect()
      boldHeritageEngravingObserver.disconnect()
    }

    productJs.destroy = destroyProductJs



    /**
     * Builds the dimension token expected by the 3D viewer.
     *
     * It combines the selected ring dimension and, when relevant,
     * prepends the current shape.
     *
     * @param {string|null} dimension - Explicit dimension override.
     * @param {string|null} shape - Explicit shape override.
     * @returns {string} Viewer dimension value.
     */
    function calculateDimensionValue(dimension, shape) {
      let value = "";
      if (!!dimension) {
        value = dimension;
      } else {
        $('.selector-wrapper').each(function(){
          if(strHasVal($(this).find('label').text(), 'Ring Dimensions')){
            value = $(this).find('select').val();
          }
        });
      }

      if (!!shape){
        if (shape !== 'Oval' || meta3D.type === 'heritage'){
          value = shape + value;
        }
      } else {
        if ($("select[name='properties[Shape]']").length > 0){
          value = $("select[name='properties[Shape]']").val() + value;
        }
      }

      return value;
    }

    /**
     * Maps a merchant-facing metal label to the viewer metal code.
     *
     * @param {string} metalValue - Optional explicit metal value.
     * @returns {string} Viewer metal code.
     */
    function getRingMetal (metalValue) {
      //Gold is default value
      let metalColor = 'yellow';

      const metals = {
        yellow: "GY",
        white: "GW",
        rose: "GR",
        green: "GG",
        silver: "SL"
      };

      if (!!metalValue){
        metalColor = metalValue;
      } else {
        $('.selector-wrapper').each(function () {
          if (strHasVal($('label', this).text(), 'Metal')) {
            metalColor = $('select', this).val().toLowerCase();
          }
        });
      }

      return Object.keys(metals, metalColor)
        .filter((key) => metalColor.toLowerCase().includes(key))
        .reduce((obj, key) => {
          return metals[key]
        }, "GY");
    }

    /**
     * Sends the current metal selection to the viewer.
     *
     * @param {string} metalValue - Selected metal label.
     * @returns {void}
     */
    function setMetal (metalValue) {
      set3dProp('metal', getRingMetal(metalValue));
    }

    /**
     * Sends the current ring dimension to the viewer.
     *
     * @param {string|null} dimension - Explicit dimension override.
     * @param {string|null} shape - Explicit shape override.
     * @returns {void}
     */
    function setDimension (dimension, shape) {
      set3dProp('dimension', calculateDimensionValue(dimension, shape));
    }

    /**
     * Applies saved artwork defaults to the corresponding Bold controls.
     *
     * @param {string} position - Viewer position label.
     * @param {string} artName - Artwork value.
     * @param {string} type - Artwork type.
     * @returns {void}
     */
    function setDefaultArtwork(position, artName, type) {
      if(!!type && !!artName) {
        if(type === 'Text'){
          $("input[name='properties[Text ("+position+")]']").val(artName);
          set3dProp('engraving', { text: artName, pos: position.toLowerCase() });
        } else {
          $("input[type='checkbox'][value='" + artName + "']").each(function () {
            if (strHasVal($(this).closest('.bold_option_swatch').find('.bold_option_title').text(), position)) {
              $(this).trigger('click');

              // Return selected class, because double click resets it
              setTimeout(function() {
                $(this).parent().addClass('bold_swatch_selected')
              }.bind(this), 600);
            }
          });
        }
      }
    }

    /**
     * Restores saved face profile engraving text.
     *
     * @param {string} position - Face profile label.
     * @param {string} text - Engraving text.
     * @returns {void}
     */
    function setDefaultFaceProfile(position, text){
      var pos = "";
      if(position === "Face Profile 1"){
        pos = 'front';
        $("input[name='properties[Face Profile 1 Text]']").val(text);
      } else if(position === "Face Profile 2"){
        pos = 'back';
        $("input[name='properties[Face Profile 2 Text]']").val(text);
      }
      if(pos !== ""){
        set3dProp('engraving', { text: text, pos: pos});
      }
    }

    /**
     * Updates the Shopify dimension selector to a saved default value.
     *
     * @param {string} dimension - Saved dimension.
     * @returns {void}
     */
    function setDefaultDimension(dimension) {
      $('.selector-wrapper').each(function () {
        if (strHasVal($(this).find('label').text(), 'Ring Dimensions')) {
          $(this).find('select').val(dimension);
        }
      });
    }

    /**
     * Updates the saved shape selector.
     *
     * @param {string} shape - Saved shape value.
     * @returns {void}
     */
    function setDefaultShape(shape){
      $("select[name='properties[Shape]']").val(shape);
    }

    /**
     * Resolves a gem option name to the hex code expected by the viewer.
     *
     * @param {string} gemName - Selected gem name.
     * @returns {string} Gem hex value or the diamond keyword.
     */
    function getGemHexaColor(gemName){
      if(gemName.includes('diamond-white')){
        return 'diamond';
      }else {
        const gemHelper = $("#gem-" + handleString(gemName));
        var gemHexa = '';
        if (gemHelper.length > 0) {
          gemHexa = gemHelper.val().replace("#", "")
        }
        return gemHexa;
      }
    }

    /**
     * Reads the preview image currently shown for a side/type pair.
     *
     * @param {string} side - Viewer side identifier.
     * @param {string} type - Preview image type.
     * @returns {string} Preview image URL.
     */
    function getPreviewImage(side, type){
      const bkgImg = $("."+side+"-preview").find(".img."+type).css("background-image");
      if(bkgImg !== 'none'){
        return bkgImg.replace('url(','').replace(')','').replace(/\"/gi, "");
      }
      return "";
    }

    /**
     * Sends one or two inside-engraving lines to the viewer.
     *
     * @param {string} textType - block or script.
     * @returns {void}
     */
    function setInsideText(textType){
      var texts = [];
      // Get real values for the text fields
      $(".bold_options").find(".bold_option").each(function() {
        var $opt = $(this);
        if (!strHasVal($opt.find('.bold_option_title').text(), 'Inside Engraving')) return;
        var isBlock = strHasVal($opt.find('.bold_option_title').text(), 'Block');
        var isScript = strHasVal($opt.find('.bold_option_title').text(), 'Script');
        if ((textType === 'block' && !isBlock) || (textType === 'script' && !isScript)) return;
        var val = $opt.find('input[type="text"]').val().trim();
        if (val.length) texts.push(val);
      });

      if(texts.length > 0){
        if(texts.length === 1){
          set3dProp('engraving', { text: texts[0], pos: 'inside', type: textType, line: 3 })
        }else{
          set3dProp('engraving', { text: texts[0], pos: 'inside', type: textType, line: 1 })
          set3dProp('engraving', { text: texts[1], pos: 'inside', type: textType, line: 2 })
        }
      }else{
        set3dProp('engraving', { pos: 'inside', reset: true });
      }
      set3dPosition('inside');
    }

    /**
     * Sends front/back profile text to the viewer based on option title.
     *
     * @param {string} optionTitle - Bold option title.
     * @returns {void}
     */
    function setFrontBackText(optionTitle){
      var text = "";
      var pos = "";
      if(strHasVal(optionTitle, "Face Profile 1")) {
        text = $(".engraving-tank-front .text span").text().trim();
        pos = "front";
      } else if(strHasVal(optionTitle, "Face Profile 2")){
        text = $(".engraving-tank-back .text span").text().trim();
        pos = "back";
      }
      set3dProp('engraving', { text: text, pos: pos});
    }

    /**
     * Applies a saved stone color to the related Bold controls.
     *
     * @param {string} colorStone - Saved color stone value.
     * @returns {void}
     */
    function setDefaultColorStone(colorStone){
      const stonesTypesRadioWrapper = $(".bold_option_title:contains('Stone Color')").closest('.bold_option_radio');
      const colorStoneSwatch = $("input[type='checkbox'][value='"+colorStone+"']");
      colorStoneSwatch.trigger('click');
      if(strHasVal(colorStoneSwatch.closest('.bold_option_swatch').find('.bold_option_title').text(), 'birthstone')){
        stonesTypesRadioWrapper.find('input[type=radio][value*="Birthstone"]').trigger('click');
      }else{
        stonesTypesRadioWrapper.find('input[type=radio]').not('[value*="Birthstone"]').trigger('click');
      }
    }

    /**
     * Applies a saved finish to the related Bold controls.
     *
     * @param {string} finish - Saved finish value.
     * @returns {void}
     */
    function setDefaultFinish(finish){
      const finishTypesRadioWrapper = $(".bold_option_title:contains('Finish')").closest('.bold_option');
      finishTypesRadioWrapper.find('input[value*="'+finish+'"]').trigger('click');
    }

    /**
     * Creates the 3D viewer iframe and synchronizes saved defaults.
     *
     * This is the central viewer bootstrap point. It builds the iframe URL,
     * removes stale frames, restores saved Bold option state, and reconnects
     * the observer used by heritage engraving swatches.
     *
     * @param {MutationObserver|null} boldOptionsObserver - Setup observer to disconnect after iframe creation.
     * @returns {void}
     */
    function init3D (boldOptionsObserver) {
      let type = meta3D?.type || 'square';
      let stoneParameter = '';
      let finishParameter = '';
      let dimension;
      let defaultDimension = meta3D?.dimension || null;
      const metalType = meta3D?.metal || getRingMetal();
      const iframeContainer = $('.tapmod-iframes-wrapper')

      //Color Stone
      if (meta3D?.colorStone) {
        setDefaultColorStone(meta3D.colorStone);
        const gemHexaColor = getGemHexaColor(meta3D.colorStone);
        if (gemHexaColor) {
          stoneParameter = '&gem=' + gemHexaColor;
        }
      }

      if (meta3D?.dimension) {
        setDefaultDimension(defaultDimension)
      }

      //Shape - Dimensions
      if (meta3D?.shape) {
        dimension = calculateDimensionValue(defaultDimension, meta3D.shape);
        setDefaultShape(meta3D.shape);
      } else {
        dimension = calculateDimensionValue(defaultDimension);
      }

      if (meta3D?.finish) {
        finishParameter = '&finish=' + meta3D.finish.toLowerCase();
        setDefaultFinish(meta3D.finish);
      }

      boldHeritageEngravingObserver.disconnect();
      iframeContainer.find('.tapmod-frame').remove();

      meta3D.iframeEl = $('<iframe>', {
        src: TAPMOD_ORIGIN + '/?type='+type+'&dimension='+dimension+'&metal='+metalType+stoneParameter+finishParameter,
        id:  'tapmod-frame-type-' + type,
        style: 'width: 100%; height: 600px; border: none;',
        class: 'tapmod-frame',
        webkitallowfullscreen: 'true',
        allow: 'autoplay; fullscreen; vr'
      }).appendTo(iframeContainer);

      if (boldOptionsObserver !== null) {
        boldOptionsObserver.disconnect();
      }

      if(meta3D?.right_artwork_type) {
        $("input[name='properties[Outside Engraving (Right)]'][value='" + meta3D.right_artwork_type + "']").each(function () {
          $(this).trigger('click');
        });
      }
      if(meta3D?.left_artwork_type) {
        $("input[name='properties[Outside Engraving (Left)]'][value='" + meta3D.left_artwork_type + "']").each(function () {
          $(this).trigger('click');
        });
      }
      if(meta3D?.face_profile_1) {
        $("input[name='properties[Face Profile 1]'][value='Text']").each(function () {
          $(this).trigger('click');
        });
      }
      if(meta3D?.face_profile_2) {
        $("input[name='properties[Face Profile 2]'][value='Text']").each(function () {
          $(this).trigger('click');
        });
      }

      const heritageTarget = document.querySelector('.bold_options .bold_option_set')
      if (heritageTarget) {
        boldHeritageEngravingObserver.observe(heritageTarget, {
          attributes: false,
          childList: true,
          subtree: true,
          characterData: false
        });
      }
    }

    var boldOptionsObserver = new MutationObserver(function(mutations, boldOptionsObserver) {
      if(meta3D?.active) {
        init3D(boldOptionsObserver);
      }
    });

    var boldHeritageEngravingObserver = new MutationObserver(function(mutations, boldOptionsObserver) {
      mutations.forEach(function(mutation){
        const mt = mutation.target;
        if(mt.classList.contains('bold_option_swatch')){
          if(mt.querySelector('.bold_option_title') != null ){
            if(strHasVal(mt.querySelector('.bold_option_title').textContent, 'Heritage Face Engraving')) {
              var hImg = mt.querySelector('.bold_option_value_swatch span').style.backgroundImage;
              if (hImg !== undefined) {
                hImg = hImg.replace('url(', '').replace(')', '').replace(/\"/gi, "");
                set3dPosition('face');
                set3dProp('artwork', {pos: 'face', url: hImg});
              }
            }
          }
        }
      });
    });

    function init () {
      if (item.hasClass('initialized')) return;

      item.data('productjs', productJs);
      initObservers();
      bindViewerEvents();
      bindMessageListener();
      initVariantStatus();
      initializeVariantSelector();
      initTotalPriceUpdate();

      item.on('galleryLoaded', function() {
        item.data('fullLoaded', true);
        item.trigger('productLoaded');
      });

      initGallery();

      // Add 3D viewer, if product has it
      if (meta3D?.active) {
        if ($(".bold_options").children().length > 0) {
          init3D(null)
        }
        else {
          boldOptionsObserver.observe(document.querySelector(".bold_options"), {
            attributes: true,
            childList: true,
            subtree: true,
            characterData: true
          });
        }
      }

      item.addClass('initialized');
    };

    /**
     * Initializes observers for product elements with custom actions.
     * 
     * @returns {void}
     */
    function initObservers () {
      var ringDimensionsObserver = new MutationObserver(function () {
        // Hiding double "Ring Dimensions" option on cold load
        $('.bold_option_title:contains("Ring Dimensions")').closest('.bold_option').hide();

        // Check if Face Engraving has single element and "hide" it
        const faceBlock = $('.bold_option_title:contains("Face Engraving")').closest('.bold_option');
        const faceItems = faceBlock.find('.bold_option_element > .bold_option_value')

        if (!faceBlock.data('processed') && faceItems.length === 1) {
          // note: set height to be able to trigger click on it (in init3D())
          faceBlock.css({ height: '1px', overflow: 'hidden', padding: 0, margin: 0 })
          faceBlock.data('processed', true)
        }
      });

      var boldOptionsEl = document.querySelector(".bold_options");
      if (boldOptionsEl) {
        ringDimensionsObserver.observe(boldOptionsEl, { childList: true, subtree: true });
      }
    }

    /**
     * Enriches variant objects with availability status and lookup maps.
     *
     * @returns {void}
     */
    function initVariantStatus() {
      $.each(product.variants, function(key, variant){
        variant.status = variant.available ? 'available' : 'soldout';
        variantByIdMap[variant.id] = variant;
        if (variant.sku) {
          variantBySkuMap[variant.sku] = variant;
        }
      });
    }

    /**
     * Filters Slick gallery slides by the currently selected variant options.
     *
     * @param {object} slickOptions - Slick options object.
     * @param {string[]} objectOptions - Product option names.
     * @param {object} optionsContainer - Selected variant object.
     * @param {jQuery} gallery - Gallery element.
     * @returns {void}
     */
    productJs.slickFilterThumbs = function(slickOptions, objectOptions, optionsContainer, gallery) {
      //Example reference:
      //slickOptions: ...
      //objectOptions: product.options
      //optionsContainer: variant;
      //gallery: jQuery object of the gallery, ex. $('.thumbs'), $('.main-images'), etc.

      var galleryOptions = productJs.config.gallery;
      var groupOptionProperties = [];
      $(galleryOptions.galleryGroupByOptions).each(function (ix, e){
        $(objectOptions).each(function(ix2,e2){
          if (Simplistic.handleize(e) == Simplistic.handleize(e2))
            groupOptionProperties.push('option'+(1+ix2))
        })
      })

      if(groupOptionProperties.length>0) {
        gallery.slick('slickUnfilter');
        gallery.find('.slideVisible').removeClass('slideVisible');

        var slideSelector = '';
        $(groupOptionProperties).each(function(ix,e){
          var part1Selector = '';
          var part2Selector = '';
          if(slideSelector=='' || galleryOptions.galleryGroupMatchMode>=2)
            part1Selector += ' .slide';

          part1Selector += '[data-'+Simplistic.handleize(galleryOptions.galleryGroupByOptions[ix])+'*="_'+Simplistic.handleize(optionsContainer[e])+'_"]';
          if (galleryOptions.galleryGroupMatchMode==4) {
            part2Selector = ', ' +part1Selector;
            $(groupOptionProperties).each(function(ix2,e2){
              if (e2!=e) {
                part1Selector += ':not([data-'+Simplistic.handleize(galleryOptions.galleryGroupByOptions[ix2])+'])';
                part2Selector += '[data-'+Simplistic.handleize(galleryOptions.galleryGroupByOptions[ix2])+'*="_'+Simplistic.handleize(optionsContainer[e2])+'_"]';
              }
            });
          }
          slideSelector += (slideSelector=='' || galleryOptions.galleryGroupMatchMode==1 ? '' : ', ') + part1Selector + part2Selector;
        });

        //Show the images that don't have any of the galleryGroupByOptions
        var withoutAltSelector = ' .slide';
        $(galleryOptions.galleryGroupByOptions).each(function(){
          withoutAltSelector += ':not([data-'+Simplistic.handleize(this)+'])';
        });
        slideSelector += ', ' + withoutAltSelector;

        /**
         * Sorts gallery slides back into their original order.
         *
         * @param {HTMLElement} a - First slide element.
         * @param {HTMLElement} b - Second slide element.
         * @returns {number} Sort comparison result.
         */
        function sortCompare(a,b){
            akey = parseInt($(a).attr("data-index"));
            bkey = parseInt($(b).attr("data-index"));
            if (akey == bkey) return 0;
            if (akey < bkey) return -1;
            if (akey > bkey) return 1;
        }

        gallery.find(slideSelector).sortDomElements(sortCompare).addClass('slideVisible');
        gallery.find('.slideVisible:eq(0) img').addClass('lazyload');
        gallery.slick('slickFilter', '.slideVisible');
        gallery.slick('slickGoTo', 0, true);
      }
    }

    /**
     * Initializes Shopify variant selectors and connects them to theme UI.
     *
     * @returns {void}
     */
    function initializeVariantSelector() {
      var variantSelectorId = "product-select-"+product.id;
      var selectCallback = onVariantSelected();

      new Shopify.OptionSelectors(variantSelectorId, { product: product, onVariantSelected: selectCallback });

      if(product.options.length > 1) {
        if(product.variants.length > 1) {
          linkedOptions.linkOptionSelectors(product);
        }

        // Add label if only one product option and it isn't 'Title'.
        if(product.options.length == 1 && product.options[0] != 'Title') {
          item.find('.selector-wrapper:eq(0)').prepend('<label>'+product.options[0]+'</label>');
        }
        item.find('.selector-wrapper label').append('*');

      } else {
        if(product.variants.length > 1) {
          item.find('.select-variant').change(function(){
            var variantSelected = false;
            $.each(product.variants, function(key, variant){
              if(variant.id==item.find('.select-variant').val()) {
                variantSelected = variant;
                return;
              }
            });
            selectCallback(variantSelected, null);
          });
        }
      }

      if (shopSettings.variants_layout == 'swatches') {
        item.find('.swatch :radio').change(function() {
          var optionIndex = $(this).closest('.swatch').attr('data-option-index');
          var optionValue = $(this).val();
          $(this).closest('form').find('.single-option-selector').eq(optionIndex).val(optionValue).trigger('change');
        });
      }

      if(product.variants.length == 1) {
        selectCallback(product.variants[0], null);
      } else {
        var firstVariant = productJs.getFirstAvailableVariant();
        if(!firstVariant){
          firstVariant = product.variants[0];
        }
        productJs.setVariant(firstVariant);
      }
    }

    /**
     * Returns the first available variant.
     *
     * @returns {object|undefined} First available variant.
     */
    productJs.getFirstAvailableVariant = function() {
      for(var i=0; i < product.variants.length; i++){
        if(product.variants[i].available)
          return product.variants[i];
      }
    };

    productJs.getVariantBySku = function(sku) {
      return variantBySkuMap[sku]
    };

    productJs.getVariantById = function(id) {
      return variantByIdMap[id]
    };

    /**
     * Returns the currently selected variant based on the form input value.
     *
     * @returns {object|boolean} Current variant or false.
     */
    productJs.getCurrentVariant = function() {
      var variantId = item.find('[name=id]').val();
        if(variantId!="") {
          return productJs.getVariantById(variantId);
        } else {
          return false;
        }
    };

    /**
     * Returns the product payload used by the controller.
     *
     * @returns {object} Shopify product object.
     */
    productJs.getProduct = function() {
      return product;
    };

    /**
     * Selects a variant in the native Shopify selectors.
     *
     * @param {object|number|string} variant - Variant object or id.
     * @returns {void}
     */
    productJs.setVariant = function(variant){
      if(variant) {
        if(!isNaN(variant)) {
          variant = productJs.getVariantById(variant);
        }
        item.find('.single-option-selector').each(function(){
          var selector = this;
          var option = $(selector).data('option');
          $(selector).find('option').each(function(){
            if(this.value == variant[option]){
              $(selector).val(this.value);
              return;
            }
          });
        });
        item.find('.single-option-selector:first').trigger('change');
      }
    };

    /**
     * Builds the Shopify variant-selected callback used by OptionSelectors.
     *
     * The callback updates Bold controls, synchronizes pricing and gallery
     * state, and keeps the product form in sync with the selected variant.
     *
     * @returns {Function} Variant selection callback.
     */
    function onVariantSelected() {
      return function(variant, selector) {
        if (!variant) {
          item.find('#product-'+product.id+'-variant').val('');
          updateButtons(null);
          $(item).trigger('variantChange', [variant]);
          return;
        }

        /* BOLD CUSTOM */
        var variantOptionVal = variant.option1;
        var index = false;

        if(typeof BOLD.options.app?.optionProducts[0] != "undefined" && typeof BOLD.options.app?.optionProducts[0].optionSets[0] != "undefined"){
          var options = BOLD.options.app.optionProducts[0].optionSets[0].options;
          $.each(options, function(key, value){
            if(value.publicName == "Ring Dimensions"){
              $(document).find("."+value.fields.uniqueClassname).closest('.bold_option').hide();
              $.each(value.fields.optionElement, function(key2, value2){
                if(value2.optionValueTitle.replace("/", " ") == variantOptionVal){
                  index = key2;
                  $(document).find("."+value.fields.uniqueClassname+":eq("+index+")").trigger("click");
                };
              });
            }
          });
        }

        /* Preview background */
        var optionIndex = 0, previewClass = false;
        for(var i=0,length=product.options.length; i<length; i++) {
          if(product.options[i] == "Material" || product.options[i] == "Color" || product.options[i] == "Metal"){
            optionIndex = i+1;
            previewClass = "bg-material-" + variant["option"+optionIndex].toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '').replace(/^-/, '');
          }
        }
        if(previewClass){
          item.find(".preview .inside-preview .text").removeClass(function (index, css) {
            return (css.match (/\bbg-material-\S+/g) || []).join(' '); // removes anything that starts with "bg-material-"
          });
          item.find(".preview .inside-preview .text").addClass(previewClass);
        }
        /* Preview background */

        if(variant && variant.id) {

          if (shopSettings.variants_layout == 'swatches') {
            //Swatches code
            var form = item.find('form');
            for(var i=0,length=variant.options.length; i<length; i++) {
              var radioButton = form.find('.swatch[data-option-index="' + i + '"] :radio[value="' + variant.options[i] +'"]');
              if(radioButton.length > 0) {
                radioButton.get(0).checked = true;
              }
            }
          }
          // If contact form, update variant name
          item.find("#variant-title").val(variant.title);

          //Update the price
          item.find('[data-price="deal"]').html(Shopify.formatMoney(variant.price, Simplistic.formatMoney).replace('.00', '').replace(' ',''));

          if(variant.compare_at_price != null && variant.compare_at_price > variant.price){
            var retailPrice = Shopify.formatMoney(variant.compare_at_price, Simplistic.formatMoney).replace('.00', '').replace(' ','');
            var percentDiscount = Math.round( (((variant.compare_at_price - variant.price) * 100 ) / variant.compare_at_price) );
            var moneyDiscount = Shopify.formatMoney((variant.compare_at_price - variant.price), Simplistic.formatMoney).replace('.00', '').replace(' ','');
            item.find('[data-price="retail"]').html(retailPrice).show();
            item.find('[data-price="off-percent"]').html(percentDiscount+'%').show();
            item.find('[data-price="off-amount"]').html(moneyDiscount).show();
          } else {
            item.find('[data-price="retail"]').hide();
            item.find('[data-price="off-percent"]').hide();
            item.find('[data-price="off-amount"]').hide();
          }

          //Filter The images by variant
          filterImages(variant);

          //Update the variant value to add to cart
          item.find('#product-'+product.id+'-variant').val(variant.id);

        } else {
          item.find('#product-'+product.id+'-variant').val("");
        }
        updateButtons(variant);

        $(item).trigger("variantChange", [variant]);
      }
    }

    /**
     * Filters product media and optionally switches the main slide.
     *
     * @param {object} variant - Active variant.
     * @param {boolean} notSwitchImg - Prevents automatic slide switching.
     * @returns {void}
     */
    function filterImages(variant, notSwitchImg) {
      if(variant) {
        //Image in grid
        if(variant.featured_image) {
          item.find('.image img').attr('src', variant.featured_image.src);
        }

        //Main image in product page
        if(item.find('.main-images .slick-track').length>0) {
          productJs.slickFilterThumbs(productJs.config.gallery.slickMainOptions, product.options, variant, item.find('.main-images'));
          if ($.inArray('3d-model', productJs.getProduct().tags) === -1) {
            const condSwitchOnLoad = shopSettings.product_image_switch_onload ? true : !notSwitchImg && item.data('fullLoaded')
            if(variant.featured_media && condSwitchOnLoad) {
              switchMainImage(variant.featured_media.id);
            }
          }
        }

        //Thumbs in product page
        if(item.find('.thumbs .slick-track').length>0) {
          productJs.slickFilterThumbs(productJs.config.gallery.slickThumbsOptions, product.options, variant, item.find('.thumbs'));
        }

      }
    }

    /**
     * Updates add-to-cart and sold-out UI states.
     *
     * @param {object|null} variant - Current variant.
     * @returns {void}
     */
    function updateButtons(variant) {
      var status;
      if(product.available) {
        status = variant ? variant.status : 'available';
      } else {
        status = 'soldout';
      }

      item.find('.buttons-wrapper .add-button, .buttons-wrapper .sold-out-button, .soldout-product-message').hide(); //Grid
      item.find('.content-available, .content-soldout, .quantity-box').hide(); //Product page
      switch(status) {
        case "available":
          item.find('.buttons-wrapper .add-button').show(); //Grid
          item.find('.content-available, .quantity-box').show(); //Product page
          break;
        case "soldout":
          item.find('.buttons-wrapper .sold-out-button').show(); //Grid
          item.find('.soldout-product-message').show(); //Grid
          item.find('.content-soldout').show(); //Product page
          break;
      }
    }

    /**
     * Navigates the main product gallery to the requested media item.
     *
     * @param {number|string} mediaId - Media identifier.
     * @returns {void}
     */
    function switchMainImage(mediaId){
        if(item.find('.main-images .slick-track').length>0) {
          var index = item.find('.main-images .slide:not(.slick-cloned)').index(item.find('.main-images .slide:not(.slick-cloned)[data-media-id="'+mediaId+'"]'));
          var nextImage = item.find('.main-images .slide:eq('+index+') img');
          if(!nextImage.hasClass('lazyloaded')) {
        	nextImage.addClass('lazyload');
          }
          item.find('.main-images').slick('goTo', index);
        }
    }

    /**
     * Initializes the main gallery, thumbs, zoom, and photoswipe behavior.
     *
     * @returns {void}
     */
    function initGallery(){
      var mainGalleryLoaded = false;
      var thumbsGalleryLoaded = false;

      //Initialize zoom on main product image
      /**
       * Initializes zoom for the active main gallery image.
       *
       * @param {number} index - Slide index.
       * @returns {void}
       */
      function initializeZoom(index) {
      	if(item.find('.product-gallery').hasClass('zoom-in') && !isMobile.any) {
            var mainProductImage = item.find('.slide:not(.slick-cloned) .large-thumb:eq('+index+')');
            if(mainProductImage.length>0 && mainProductImage.parent().find('.zoomImg').length==0) {
              var zoomedSrc = mainProductImage.data('file');
              if(zoomedSrc && zoomedSrc!="") {
                mainProductImage.wrap('<span style="display:block"></span>').css('display', 'block').parent().zoom({
                  url: zoomedSrc,
                  callback: function(){
                    item.find('.active-wrapper img.zoomImg').prop('alt', '');
                  }
                });
              }
            }
        }
      }

      item.find('.main-images').on('beforeChange', function(event, slick, currentSlide, nextSlide){
        var nextImage = $(this).find('.slide:not(.slick-cloned) .large-thumb:eq('+nextSlide+')');
        if(!nextImage.hasClass('lazyloaded')) {
        	nextImage.addClass('lazyload');
        }

        if(item.find('.thumbs .slick-track').length>0) {
          item.find('.thumbs.switch .slide').removeClass('active');
          item.find('.thumbs.switch .slide:not(.slick-cloned):eq('+nextSlide+')').addClass('active');

          var slidesToShow = item.find('.thumbs.switch').slick('slickGetOption', 'slidesToShow');
          if(slidesToShow==1) {
            item.find('.thumbs.switch').slick('slickGoTo', nextSlide);
          } else {
              item.find('.thumbs.switch').slick('slickGoTo', nextSlide-Math.floor((slidesToShow-1)/2));
          }
        }

        initializeZoom(nextSlide);
      });
      Simplistic.onImagesLoaded(item.find('.main-images img'), function(){
        item.find('.main-images').slick(productJs.config.gallery.slickMainOptions);
        filterImages(productJs.getCurrentVariant(), true);
        initializeZoom(item.find('.active-wrapper:not(.slick-cloned)').index(item.find('.active-wrapper.slick-active')));
        mainGalleryLoaded = true;
        if(mainGalleryLoaded && thumbsGalleryLoaded) {
          item.trigger('galleryLoaded');
        }
      });

      //Initialize thumbs on main product page
      item.find('.thumbs.switch a.gallery').on('click', function(e) {
        e.preventDefault();

        if(item.find('.thumbs .slick-track').length > 0) {
          item.find('.thumbs.switch .slide').removeClass('active');
          $(this).closest('.slide:not(.slick-cloned)').addClass('active');
        }

        let currentVariantId = $(this).data('variant-id');
        if ($.inArray('3d-model', productJs.getProduct().tags) !== -1) {
          $.each(productJs.getProduct().variants, function(key, variant) {
            if (variant.id == currentVariantId) {
              if (variant.option1 != null) {
                setDimension(variant.option1)
              }
              if (variant.option2 != null) {
                setMetal(variant.option2);
              }
              return false;
            }
          });
        }
        else {
          switchMainImage($(this).data('media-id'));
          var variantId = $(this).attr('variant-id');
          if(variantId && variantId!='') {
            if(product.options.length <= 1) {
              productJs.setVariant(variantId);
            }
          }
        }
      });
      Simplistic.onImagesLoaded(item.find('.thumbs img'), function(){
        item.find('.thumbs').slick(productJs.config.gallery.slickThumbsOptions);
        item.find('.thumbs .slide:not(.slick-cloned):eq(0)').addClass('active');
        filterImages(productJs.getCurrentVariant(), true);
        thumbsGalleryLoaded = true;
        if(mainGalleryLoaded && thumbsGalleryLoaded) {
          item.trigger('galleryLoaded');
        }
      });

      //Initialize photoswipe on main product image
      if($('.active-wrapper a.productImage').length > 0) {
        item.find('.active-wrapper a.productImage, .active-wrapper video, .active-wrapper iframe').photoSwipe();
      }
    }

    /**
     * Handles Bold option changes and mirrors them to the 3D viewer.
     *
     * This function routes multiple Bold control types to the viewer by
     * inspecting the option title, current value, and preview state.
     *
     * @param {Event} e - Change event from a Bold control.
     * @returns {Promise<void>} Resolves when async processing is complete.
     */
    async function handleBoldOptionChange(e) {
      if (!$(e.target).closest('.bold_option:visible').length) return

      const boldOptionTitle = getBoldOptionTitle(e.target)
      const param = String($(e.target).val() || '').toLowerCase()
      const { selected } = window.meta3D

      /**
       * Checks whether the current Bold option title contains a token.
       *
       * @param {string} name - Token to find.
       * @returns {boolean} True when the title contains the token.
       */
      function titleHas (name) {
        return strHasVal(boldOptionTitle, name)
      }

      let side = getSideFromTitle(boldOptionTitle)
      if (side === 'face') {
        side = ''
      }

      if (titleHas('Shape')) {
        setDimension(null, $(e.target).val())
      }

      if (titleHas('Finish')) {
        set3dProp('finish', param.trim())
      }

      if (titleHas('Face Engraving')) {
        set3dPosition('face')

        if (strHasVal(param, 'school') && !!selected.face_school) {
          set3dProp('artwork', { pos: 'face', url: selected.face_school })
        }
        if (strHasVal(param, 'upload')) {
          faceUploadExtend()

          if (!!selected.face_upload) {
            set3dProp('artwork', { pos: 'face', base: selected.face_upload })
          }
        }
      }

      if (titleHas('Face Engravings')) {
        let img = $(e.target).closest('.bold_option_value_element').find('.bold_option_value_swatch span').css('background-image')
        if (img !== undefined) {
          img = img.replace('url(', '').replace(')', '').replace(/\"/gi, '')
          set3dPosition('face')
          set3dProp('artwork', { pos: 'face', url: img })
          selected.face_school = img
        }
      }

      if (titleHas('Upload Artwork (Face)') || titleHas('Upload Face Artwork')) {
        set3dPosition('face')
        if (e.target?.files && e.target?.files[0] instanceof Blob) {
          const base = await fileToBase64(e.target.files[0])
          selected.face_upload_orig = base
          await processFaceUpload(base)
        }
      }

      if (titleHas('Inside Engraving')) {
        let textType = ''

        if (strHasVal(param, 'none')) {
          set3dPosition('inside')
          set3dProp('engraving', { pos: 'inside', reset: true })
        } else if (strHasVal(param, 'block')) {
          textType = 'block'
        } else if (strHasVal(param, 'script')) {
          textType = 'script'
        }

        if (textType) {
          setInsideText(textType)
        }
      }

      if (titleHas('Graduation Year')) {
        if (strHasVal(param, 'none')) {
          set3dPosition('face')
          set3dProp('engraving', { pos: 'outside', reset: true })
        } else if (strHasVal(param, 'year')) {
          set3dPosition('face')
          set3dProp('engraving', { text: $('.year-preview .text span').text(), pos: 'outside' })
        }
      }

      if (titleHas('Outside Engraving') && !!side) {
        set3dPosition(side)
        set3dProp('artwork', { pos: side, reset: true })

        if (strHasVal(param, 'upload artwork')) {
          const lastUploadArtwork = getPreviewImage(side, 'upload')
          if (lastUploadArtwork) {
            set3dProp('artwork', { pos: side, base: lastUploadArtwork })
          }
        } else if (strHasVal(param, 'text')) {
          const lastArtworkText = $('#last-artwork-text-' + side).val()
          if (lastArtworkText) {
            set3dProp('engraving', { text: lastArtworkText, pos: side })
          }
        } else if (strHasVal(param, 'artwork')) {
          const lastPreArtwork = strHasVal(param, 'general') ? getPreviewImage(side, 'general') : getPreviewImage(side, 'school')
          if (lastPreArtwork) {
            set3dProp('artwork', { pos: side, url: lastPreArtwork })
          }
        }
      }

      if (titleHas('Face Profile') && !!side) {
        set3dPosition(side)
        if (strHasVal(param, 'none')) {
          set3dProp('engraving', { pos: side, reset: true })
        } else if (strHasVal(param, 'text')) {
          setFrontBackText(boldOptionTitle)
        }
      }

      if (titleHas('Artwork') && !!side) {
        set3dPosition(side)
        if ($(e.target).closest('.bold_option_value_element').find('.bold_option_value_swatch span').length > 0) {
          var img = $(e.target).closest('.bold_option_value_element').find('.bold_option_value_swatch span').css('background-image')
          img = img.replace('url(', '').replace(')', '').replace(/\"/gi, '')
          set3dProp('artwork', { pos: side, url: img })
        } else if ($(e.target)[0].type === 'file' && e.target.files[0] instanceof Blob) {
          const imgBase = await fileToBase64(e.target.files[0])
          set3dProp('artwork', { pos: side, base: imgBase })
        }
      }

      if (titleHas('Color')) {
        const gemName = handleString(param)
        const gemHexaColor = getGemHexaColor(gemName)

        if (gemHexaColor) {
          set3dPosition('face')
          set3dProp('gem', gemHexaColor)
        }
      }
    }

    /**
     * Handles live text input for engraving-related Bold fields.
     *
     * @param {KeyboardEvent} e - Keyup event from a Bold text input.
     * @returns {void}
     */
    function handleBoldTextInput(e) {
      const boldOptionTitle = getBoldOptionTitle(e.target)
      const side = getSideFromTitle(boldOptionTitle)
      side && set3dPosition(side)

      if (strHasVal(boldOptionTitle, 'Inside Engraving')) {
        var textType = strHasVal(boldOptionTitle, 'Script') ? 'script' : 'block'
        setInsideText(textType)
        return
      }

      if (!side) return

      if (strHasVal(boldOptionTitle, 'Text')) {
        $('#last-artwork-text-' + side).val($(e.target).val())
        set3dProp('engraving', { text: $(e.target).val(), pos: side })
      } else if (strHasVal(boldOptionTitle, 'Year')) {
        set3dProp('engraving', { text: $(e.target).val(), pos: 'outside' })
      }
    }

    /**
     * Starts observing the Bold price display and keeps the total in sync.
     *
     * @returns {void}
     */
    function initTotalPriceUpdate() {
      const priceEl = document.querySelector(priceContainer);

      if (priceEl) {
        scheduleTotalPriceUpdate();

        if (priceObserver) {
          priceObserver.disconnect();
        }

        priceObserver = new MutationObserver(() => {
          scheduleTotalPriceUpdate();
        });

        priceObserver.observe(priceEl, {
          childList: true,
          characterData: true,
          subtree: true
        });
      }
    }

    /**
     * Extends the face upload option with helper content and margin toggle.
     *
     * @returns {void}
     */
    function faceUploadExtend() {
      const cont = $('.bold_option_uploadfile .bold_option_title:contains("Face")').closest('.bold_option')

      if (cont.length && !cont.data('once')) {
        const guideLink = 'https://cdn.shopify.com/s/files/1/0467/5364/4698/files/Guide_for_Custom_Ring_Engraving_Artwork_Submission.pdf?v=1729712616'
        const text = 'Custom designs will be evaluated by our team before engraving. Please upload your artwork file as a .jpg or a .png in black and white, no larger than 1,000 px by 1,000 px.'
        const proTip = `<div class="protip"><span>Pro tip</span> For optimal results, refer to <a href="${guideLink}" target="_blank">our guide</a> for custom ring engraving artwork submission.</div>`
        const note = $('<div class="bold_option_note">' + text + proTip + '</div>')

        const checkbox = $('<label style="display:block;"><input type="checkbox" id="face-upl-add-space">&nbsp; Increase Margins <em>(Optional)</em></label>')
        checkbox.on('change', function () {
          processFaceUpload(meta3D.selected.face_upload_orig);
        })

        cont.append(note).append(checkbox)
        cont.data('once', true) // Set only once
      }
    }

    /**
     * Processes the uploaded face artwork before sending it to the viewer.
     *
     * @param {string} img - Uploaded image data URL.
     * @returns {Promise<void>} Resolves after the viewer is updated.
     */
    async function processFaceUpload (img) {
      if (!img) return;
      const { selected } = window.meta3D

      // Add big margins, if "Safety margins" is checked
      const margin = $('#face-upl-add-space').prop('checked') ? 140 : 30
      img = await faceImgAddMargins(img, margin, true);

      set3dProp('artwork', { pos: 'face', base: img });
      selected.face_upload = img; // Set 'cache'

      // const dbgImg = $('<img style="width: 100%;">').attr('src', img);
      // $('.product-info .description').append(dbgImg);
    }

    /**
     * Resizes an image into the viewer canvas with optional mask overlay.
     *
     * @param {string} base64Image - Source data URL.
     * @param {number} margin - Inner margin size in pixels.
     * @param {boolean} mask - Whether the oval mask should be applied.
     * @returns {Promise<string>} Processed image as a data URL.
     */
    function faceImgAddMargins(base64Image, margin = 50, mask = false) {
      const W = 1024;
      const H = 1162;
      const imagePromises = [loadImage(base64Image)]

      if (mask) {
        const { masks } = window.meta3D
        imagePromises.push(loadImage(masks.oval))
      }

      return Promise.all(imagePromises).then(function(images) {
        const img = images[0]
        const maskImg = images[1]
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = W
        canvas.height = H

        const maxW = margin ? W - (2 * margin) : W
        const maxH = margin ? H - (2 * margin) : H
        const scale = Math.min(maxW / img.width, maxH / img.height)
        const width = img.width * scale
        const height = img.height * scale
        const x = (W - width) / 2
        const y = (H - height) / 2

        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, W, H)
        ctx.drawImage(img, x, y, width, height)

        if (maskImg) {
          ctx.globalCompositeOperation = 'source-over'
          ctx.drawImage(maskImg, 0, 0, W, H)
        }

        return canvas.toDataURL()
      })
    }

    /**
     * Get total price from string: "$200 + $44.00..."
     *
     * @param {string} priceString
      * @returns {number} Parsed total price.
     */
    function extractTotalPrice(priceString) {
      if (!priceString) return 0;

      try {
        const matchedPrices = priceString.match(/\$[\d,]+(?:\.\d+)?/g)
        if (!matchedPrices) {
          return 0
        }

        const prices = matchedPrices.map(price =>
          parseFloat(price.replace('$', '').replace(/,/g, ''))
        );

        return Number(prices.reduce((a, b) => a + b, 0).toFixed(2));
      } catch (error) {
        console.error('Cannot extract total price:', error);
        return 0;
      }
    }

    /**
     * Update one total price from price parts (main + paid options/variants).
     * Based on bug in SC Product Options.
     * See "SC Product Options -> Settings -> Frontend Settings"
     *
     * @returns {void}
     */
    function updateTotalPrice() {
      const total = extractTotalPrice($(priceContainer).first().text());
      $('.bold_option_price_total').text(`$${total.toLocaleString('en-US')}`);
    }

    // Initialize linked product options helper from shopify-helpers.js
    const linkedOptions = createLinkedProductOptions(item, optionsMap, optionsMapAll);

    return init();
}
